/**
 * ETV-Einladung edit/view: UI fields, mark-as-sent (edit), Nachrichtenversand (view).
 * Fetch intercept: lib/luxFetchHandlers/ownersMeetingInvitation.js
 * Shared fetch state: lib/fetch/ownersMeetingInvitation.js
 */
const LUX_OWNERS_MEETING_INVITATION_NACHRICHTENVERSAND_BTN_ID = 'lux-owners-meeting-invitation-nachrichtenversand-btn'
const LUX_OWNERS_MEETING_INVITATION_MARK_SENT_BTN_ID = 'lux-om-invitation-mark-sent-btn'
const LUX_MARK_AS_SENT_DIALOG_ID = 'lux-om-invitation-mark-sent-dialog'

/** Must match entryPoints.js ownersMeetingInvitationPage regex (view segment). */
const OWNERS_MEETING_INVITATION_VIEW_PATH_RE = /^\/owners-meeting\/invitation\/view\/[A-Za-z0-9]+$/
/** Edit page only — numeric invitation id. */
const OWNERS_MEETING_INVITATION_EDIT_PATH_RE = /^\/owners-meeting\/invitation\/edit\/(\d+)$/

const MARK_AS_SENT_STATUS_REFRESH_MS = 3000

const MARK_AS_SENT_NO_MESSAGES_HINT =
  'Impower setzt eine Einladung erst auf versendet, wenn Nachrichten dafür existieren. Bitte zuerst über „Vorschau & Versenden“ versenden.'

const markAsSentStatusCache = {
  invitationId: null,
  status: null,
  /** null = unknown (lookup failed); Impower is then left to reject the call. */
  hasMessages: null,
  loaded: false,
  fetchedAt: 0,
}
let markAsSentStatusFetchSeq = 0
let markAsSentStatusFetchInFlight = null

function invalidateMarkAsSentStatusCache() {
  markAsSentStatusCache.loaded = false
  markAsSentStatusCache.fetchedAt = 0
}

function shouldRefreshMarkAsSentStatus(invitationId) {
  if (markAsSentStatusCache.invitationId !== invitationId) return true
  if (markAsSentStatusCache.loaded && markAsSentStatusCache.status === 'SENT') return false
  if (
    markAsSentStatusCache.loaded &&
    markAsSentStatusCache.fetchedAt &&
    Date.now() - markAsSentStatusCache.fetchedAt < MARK_AS_SENT_STATUS_REFRESH_MS
  ) {
    return false
  }
  return true
}

function bindVorschauVersendenStatusRefresh(vorschauVersendenBtn) {
  if (!vorschauVersendenBtn || vorschauVersendenBtn.dataset.luxMarkSentInvalidateBound === '1') return
  vorschauVersendenBtn.dataset.luxMarkSentInvalidateBound = '1'
  vorschauVersendenBtn.addEventListener('click', () => {
    invalidateMarkAsSentStatusCache()
  })
}

function applyMarkAsSentButtonState(button, invitationId, status, hasMessages = null) {
  if (!button || getEditInvitationIdFromPath() !== invitationId) return

  if (status === 'SENT') {
    button.disabled = true
    button.title = 'Einladung ist bereits als versendet markiert.'
  } else if (hasMessages === false) {
    button.disabled = true
    button.title = MARK_AS_SENT_NO_MESSAGES_HINT
  } else if (button.dataset.luxLoading !== '1') {
    button.disabled = false
    button.title = ''
  }
}

function getViewInvitationIdFromPath() {
  const m = window.location.pathname.match(/\/owners-meeting\/invitation\/view\/([A-Za-z0-9]+)$/)
  return m?.[1] ?? null
}

function getEditInvitationIdFromPath() {
  const m = window.location.pathname.match(OWNERS_MEETING_INVITATION_EDIT_PATH_RE)
  return m ? parseInt(m[1], 10) : null
}

function getImpowerAuthToken() {
  try {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}')
    return auth?.token || null
  } catch {
    return null
  }
}

async function fetchOwnersMeetingInvitationFromImpower(invitationId) {
  const token = getImpowerAuthToken()
  if (!token || !invitationId) return null

  const doFetch = window.luxNativeFetch || window.fetch
  const res = await doFetch(
    `/services/pmp-accounting/api/v1/owners-meeting-invitations/${encodeURIComponent(String(invitationId))}`,
    {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'same-origin',
    },
  )
  if (!res.ok) return null
  return res.json()
}

/** @returns {Promise<boolean|null>} null when the lookup failed */
async function fetchOwnersMeetingInvitationHasMessages(invitationId) {
  const token = getImpowerAuthToken()
  if (!token || !invitationId) return null

  // Impower message search expects a single comma-separated `state` value (not repeated params).
  const params = new URLSearchParams({
    sourceId: String(invitationId),
    sourceType: 'OM_INVITE',
    size: '1',
    state: ['DRAFT', 'GENERATING', 'READY', 'SENDING', 'SENT', 'FAILED'].join(','),
  })
  const doFetch = window.luxNativeFetch || window.fetch
  const res = await doFetch(`/services/pmp-accounting/api/v1/messages?${params}`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'same-origin',
  })
  if (!res.ok) return null
  const data = await res.json()
  const content = Array.isArray(data) ? data : data?.content
  return Array.isArray(content) ? content.length > 0 : null
}

async function postOwnersMeetingInvitationProtocol(invitationId) {
  const token = getImpowerAuthToken()
  if (!token || !invitationId) {
    throw new Error('Nicht angemeldet oder ungültige Einladung.')
  }

  const doFetch = window.luxNativeFetch || window.fetch
  const res = await doFetch(
    `/services/pmp-accounting/api/v1/owners-meeting-invitations/${encodeURIComponent(String(invitationId))}/protocol`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'same-origin',
    },
  )
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    if (/no messages exist/i.test(detail)) {
      throw new Error(MARK_AS_SENT_NO_MESSAGES_HINT)
    }
    throw new Error(detail || `HTTP ${res.status}`)
  }
}

function findPageHeaderActionArea() {
  const pageHeader =
    document.querySelector('.PageHeader[data-testid="page-header"]') ||
    document.querySelector('.PageHeader')
  return pageHeader?.querySelector('.right-side') || null
}

function clonePageHeaderButton(templateBtn, { id, label, classNameFallback }) {
  const newButton = templateBtn.cloneNode(true)
  newButton.id = id
  newButton.type = 'button'
  newButton.className = (templateBtn.className || classNameFallback || 'ant-btn ant-btn-default Button')
    .split(/\s+/)
    .filter((c) => c && c !== 'undefined')
    .join(' ')
  newButton.querySelector('.ant-btn-icon')?.remove()
  const labelSpan = Array.from(newButton.children).find(
    (el) => el.tagName === 'SPAN' && !el.classList.contains('anticon'),
  )
  if (labelSpan) {
    labelSpan.textContent = label
  } else {
    newButton.textContent = label
  }
  return newButton
}

function isExcludedHeaderTemplateButton(button) {
  const label = (button?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase()
  if (String(button?.id || '').startsWith('lux-')) return true
  return (
    label === 'als entwurf speichern' ||
    label === 'vorschau & versenden' ||
    label === 'als versendet markieren' ||
    label === 'nachrichtenversand'
  )
}

const DEFAULT_HEADER_BUTTON_CLASS = 'ant-btn ant-btn-default Button'

function findDefaultStyleHeaderButton(rightSide) {
  const buttons = Array.from(rightSide.querySelectorAll('button'))
  return (
    buttons.find((b) => (b.textContent || '').trim() === 'Kopieren') ||
    buttons.find((b) => (b.textContent || '').trim() === 'Nachrichten ansehen') ||
    buttons.find(
      (b) => !isExcludedHeaderTemplateButton(b) && b.classList.contains('ant-btn-default'),
    ) ||
    null
  )
}

function createLuxHeaderButton({ id, label, templateBtn, className = DEFAULT_HEADER_BUTTON_CLASS }) {
  if (templateBtn && !isExcludedHeaderTemplateButton(templateBtn)) {
    return clonePageHeaderButton(templateBtn, { id, label, classNameFallback: className })
  }

  const button = document.createElement('button')
  button.type = 'button'
  button.id = id
  button.className = className
  const labelSpan = document.createElement('span')
  labelSpan.textContent = label
  button.appendChild(labelSpan)
  return button
}

function findHeaderButtonByLabel(rightSide, label) {
  return (
    Array.from(rightSide.querySelectorAll('button')).find(
      (b) => (b.textContent || '').trim() === label,
    ) || null
  )
}

function resetMarkAsSentButtonForInvitationChange(button, invitationId) {
  if (!button) return

  button.dataset.luxMarkSentInvitationId = String(invitationId)
  if (button.dataset.luxLoading === '1') {
    button.dataset.luxLoading = '0'
    const labelSpan = Array.from(button.children).find(
      (el) => el.tagName === 'SPAN' && !el.classList.contains('anticon'),
    )
    if (labelSpan) {
      labelSpan.textContent = 'Als versendet markieren'
    } else {
      button.textContent = 'Als versendet markieren'
    }
  }
  button.disabled = false
  button.title = ''
}

async function applyMarkAsSentButtonAvailability(button, invitationId) {
  if (!button || !invitationId) return
  if (getEditInvitationIdFromPath() !== invitationId) return

  const hasCachedStateForInvitation =
    markAsSentStatusCache.invitationId === invitationId && markAsSentStatusCache.loaded
  const invitationChanged = button.dataset.luxMarkSentInvitationId !== String(invitationId)

  if (invitationChanged && !hasCachedStateForInvitation) {
    resetMarkAsSentButtonForInvitationChange(button, invitationId)
  } else if (invitationChanged) {
    button.dataset.luxMarkSentInvitationId = String(invitationId)
  }

  if (hasCachedStateForInvitation) {
    applyMarkAsSentButtonState(
      button,
      invitationId,
      markAsSentStatusCache.status,
      markAsSentStatusCache.hasMessages,
    )
  }

  if (!shouldRefreshMarkAsSentStatus(invitationId)) return

  if (
    markAsSentStatusFetchInFlight &&
    markAsSentStatusFetchInFlight.invitationId === invitationId
  ) {
    await markAsSentStatusFetchInFlight.promise
    if (markAsSentStatusCache.invitationId === invitationId && markAsSentStatusCache.loaded) {
      applyMarkAsSentButtonState(
        button,
        invitationId,
        markAsSentStatusCache.status,
        markAsSentStatusCache.hasMessages,
      )
    }
    return
  }

  const fetchSeq = ++markAsSentStatusFetchSeq
  const fetchPromise = (async () => {
    try {
      const [data, hasMessages] = await Promise.all([
        fetchOwnersMeetingInvitationFromImpower(invitationId),
        fetchOwnersMeetingInvitationHasMessages(invitationId).catch(() => null),
      ])
      if (fetchSeq !== markAsSentStatusFetchSeq) return
      if (getEditInvitationIdFromPath() !== invitationId) return

      markAsSentStatusCache.fetchedAt = Date.now()
      if (!data) {
        markAsSentStatusCache.loaded = false
        return
      }

      const status = String(data?.status || '').trim().toUpperCase()
      markAsSentStatusCache.invitationId = invitationId
      markAsSentStatusCache.status = status
      markAsSentStatusCache.hasMessages = hasMessages
      markAsSentStatusCache.loaded = true
      applyMarkAsSentButtonState(button, invitationId, status, hasMessages)
    } catch (error) {
      if (fetchSeq !== markAsSentStatusFetchSeq) return
      if (getEditInvitationIdFromPath() !== invitationId) return
      markAsSentStatusCache.fetchedAt = Date.now()
      markAsSentStatusCache.loaded = false
      console.error('Error loading owners meeting invitation status:', error.message)
    }
  })()

  markAsSentStatusFetchInFlight = { invitationId, promise: fetchPromise }
  try {
    await fetchPromise
  } finally {
    if (markAsSentStatusFetchInFlight?.invitationId === invitationId) {
      markAsSentStatusFetchInFlight = null
    }
  }
}

function onOwnersMeetingInvitationPageMatch() {
  const isViewPage = OWNERS_MEETING_INVITATION_VIEW_PATH_RE.test(window.location.pathname)

  if (isViewPage) {
    initializeViewPageNachrichtenversandButton()
    const viewPageObserver = new MutationObserver(() => {
      initializeViewPageNachrichtenversandButton()
    })
    viewPageObserver.observe(document.body, { childList: true, subtree: true })
    return
  }

  function syncLastDocumentDateFromInput(input, { markDirty = false } = {}) {
    luxOwnersMeetingInvitationFetchState.lastDocumentDate =
      (typeof input?.getValue === 'function' ? input.getValue() : input?.value)?.trim() ?? ''
    if (markDirty) luxOwnersMeetingInvitationFetchState.documentDateDirty = true
  }

  function attachDocumentDateListeners(input) {
    if (!input || input.dataset.luxDocumentDateBound === '1') return
    input.dataset.luxDocumentDateBound = '1'
    const sync = () => syncLastDocumentDateFromInput(input, { markDirty: true })
    input.addEventListener('input', sync)
    input.addEventListener('change', sync)
    input.addEventListener('blur', sync)
  }

  function applyHgaCheckedState(input, checked) {
    luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners = Boolean(checked)
    if (!input) return
    input.checked = luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners
    const ant = input.closest('.ant-checkbox')
    const wrap = input.closest('.ant-checkbox-wrapper')
    ant?.classList.toggle(
      'ant-checkbox-checked',
      luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners,
    )
    wrap?.classList.toggle(
      'ant-checkbox-wrapper-checked',
      luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners,
    )
  }

  function attachHgaListeners(input) {
    if (!input || input.dataset.luxHgaBound === '1') return
    input.dataset.luxHgaBound = '1'
    input.addEventListener('change', (e) => {
      applyHgaCheckedState(e.target, e.target.checked)
    })
  }

  async function loadInvitationDataFromDb(invitationId) {
    const auth = JSON.parse(localStorage.getItem('auth') || '{}')
    const token = auth?.token || null

    if (!token || !invitationId) return
    try {
      const doFetch = window.luxNativeFetch || window.fetch
      const res = await doFetch(
        `/api/impowerX/owners-meeting-invitation?invitationId=${encodeURIComponent(String(invitationId))}`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: 'same-origin' },
      )
      if (!res.ok) return
      const data = await res.json()
      const d = data?.documentDate
      luxOwnersMeetingInvitationFetchState.lastDocumentDate =
        d != null && typeof d === 'string' && d.trim() !== '' ? d.trim().slice(0, 10) : ''
      luxOwnersMeetingInvitationFetchState.documentDateDirty = false
      luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners =
        typeof data?.includeInformationForOwners === 'boolean'
          ? data?.includeInformationForOwners
          : true

      const dateInput = document.querySelector('[data-field-name="documentDate"] input')
      if (dateInput) {
        if (typeof dateInput.setValue === 'function') {
          dateInput.setValue(luxOwnersMeetingInvitationFetchState.lastDocumentDate)
        } else {
          dateInput.value = luxOwnersMeetingInvitationFetchState.lastDocumentDate
        }
      }

      const hgaInput = document.querySelector('#impowerx-hga-checkbox-input')
      applyHgaCheckedState(
        hgaInput,
        luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners,
      )
    } catch (error) {
      console.error('Error loading invitation data from DB:', error.message)
    }
  }

  const invitationIdOnLoad = getEditInvitationIdFromPath()
  if (invitationIdOnLoad) {
    loadInvitationDataFromDb(invitationIdOnLoad)
  }

  const initializeDateFieldDocumentDate = () => {
    const subSectionContent = document.querySelector('.cover-letter-subsection')
    if (!subSectionContent) {
      return false
    }

    const existing = subSectionContent.querySelector('[data-field-name="documentDate"]')
    if (existing) {
      const input = existing.querySelector('input')
      attachDocumentDateListeners(input)
      // Sync from state when clean: fill when server has a date, and clear when it returns empty
      // (fill-if-empty alone left the previous invitation's date in the lux-picker).
      if (!luxOwnersMeetingInvitationFetchState.documentDateDirty && input) {
        const current =
          (typeof input.getValue === 'function' ? input.getValue() : input.value)?.trim() ?? ''
        const target = luxOwnersMeetingInvitationFetchState.lastDocumentDate || ''
        if (current !== target) {
          if (typeof input.setValue === 'function') {
            input.setValue(target)
          } else {
            input.value = target
          }
        }
      }
      // Do not sync from DOM here — mask/DOM mutations would wipe lastDocumentDate while typing.
      return true
    }

    const rows = subSectionContent.querySelectorAll('.cover-letter-subsection-element')
    const lastRow = rows[rows.length - 1]
    if (!lastRow) {
      return false
    }

    const documentDateField = generateInputField({
      id: 'documentDate',
      fieldType: 'DateInput',
      name: 'documentDate',
      label: 'Datum für das Anschreiben',
      value: luxOwnersMeetingInvitationFetchState.lastDocumentDate || '',
      isDisabled: false,
    })
    documentDateField.classList.remove('ehv-element')
    documentDateField.dataset.fieldName = 'documentDate'
    const input = documentDateField.querySelector('input')
    input?.setAttribute('data-field-name', 'documentDate')
    attachDocumentDateListeners(input)
    syncLastDocumentDateFromInput(input)

    lastRow.after(documentDateField)
  }

  const initializeCheckboxStandardDocuments = () => {
    const subSectionContent = document.querySelector('#standardDocuments .sub-section-content')
    if (!subSectionContent) {
      return false
    }

    const firstRow = subSectionContent.querySelector('.sub-section-content-row')
    if (!firstRow) {
      return false
    }

    const existing = firstRow.querySelector('#impowerx-hga-checkbox')
    if (existing) {
      const hgaInput = existing.querySelector('#impowerx-hga-checkbox-input')
      attachHgaListeners(hgaInput)
      if (
        hgaInput &&
        hgaInput.checked !== luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners
      ) {
        applyHgaCheckedState(
          hgaInput,
          luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners,
        )
      }
      return true
    }

    //const checkbox = document.createElement()
    const checkbox = document.createElement('div')
    checkbox.classList.add('sub-section-content-column')
    checkbox.style.minWidth = '25rem'
    checkbox.id = 'impowerx-hga-checkbox'
    checkbox.innerHTML = `<div class="CheckboxInput">
      <div class="ant-row ant-form-item ant-form-item-with-help ant-form-item-has-success" style="row-gap: 0px;">
        <div class="ant-col ant-form-item-control">
          <div class="ant-form-item-control-input">
            <div class="ant-form-item-control-input-content">
              <label class="ant-checkbox-wrapper ant-checkbox-wrapper-in-form-item"> 
                <span class="ant-checkbox">
                  <input id="impowerx-hga-checkbox-input" type="checkbox" class="ant-checkbox-input">
                  <span class="ant-checkbox-inner"></span>
                </span>
                <span>Hausgeldabrechnung</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>`

    firstRow.appendChild(checkbox)

    const hgaInput = checkbox.querySelector('#impowerx-hga-checkbox-input')
    applyHgaCheckedState(
      hgaInput,
      luxOwnersMeetingInvitationFetchState.lastIncludeInformationForOwners,
    )
    attachHgaListeners(hgaInput)
  }

  initializeDateFieldDocumentDate()
  initializeCheckboxStandardDocuments()
  initializeEditPageMarkAsSentButton()

  // Set up MutationObserver to watch for DOM changes
  // Keep observer running so it can re-initialize the field if elements are removed/re-added during navigation
  const observer = new MutationObserver(() => {
    initializeDateFieldDocumentDate()
    initializeCheckboxStandardDocuments()
    initializeEditPageMarkAsSentButton()
  })

  // Start observing the document body for changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  })
}

function showMarkAsSentConfirmDialog() {
  if (document.getElementById(LUX_MARK_AS_SENT_DIALOG_ID)) {
    return Promise.resolve(false)
  }

  return new Promise((resolve) => {
    const overlay = document.createElement('div')
    overlay.id = LUX_MARK_AS_SENT_DIALOG_ID
    overlay.style.cssText =
      'position:fixed;inset:0;background:rgba(0,0,0,0.45);z-index:10000;display:flex;align-items:center;justify-content:center;'

    const box = document.createElement('div')
    box.style.cssText =
      'background:#fff;padding:24px 28px;border-radius:8px;box-shadow:0 8px 32px rgba(0,0,0,0.12);min-width:420px;max-width:90vw;'
    box.setAttribute('role', 'dialog')
    box.setAttribute('aria-modal', 'true')
    box.setAttribute('aria-labelledby', 'lux-om-mark-sent-dialog-title')

    const title = document.createElement('h2')
    title.id = 'lux-om-mark-sent-dialog-title'
    title.style.cssText = 'margin:0 0 18px;font-size:1.75rem;font-weight:700;color:#1a1a1a;line-height:1.3;'
    title.textContent = 'Als versendet markieren'

    const message = document.createElement('p')
    message.style.cssText = 'margin:0 0 32px;font-size:1.5rem;color:#555;line-height:1.6;'
    message.textContent =
      'Die Einladung wird als versendet markiert, ohne neue Briefe oder E-Mails zu versenden.'

    const footer = document.createElement('div')
    footer.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;'

    const rightSide = findPageHeaderActionArea()
    const vorschauVersendenBtn =
      rightSide && findHeaderButtonByLabel(rightSide, 'Vorschau & Versenden')
    const cancelBtn = createLuxHeaderButton({
      id: '',
      label: 'Abbrechen',
      templateBtn: findDefaultStyleHeaderButton(rightSide),
    })
    const confirmBtn = vorschauVersendenBtn
      ? clonePageHeaderButton(vorschauVersendenBtn, {
          id: '',
          label: 'Als versendet markieren',
          classNameFallback: vorschauVersendenBtn.className,
        })
      : createLuxHeaderButton({
          id: '',
          label: 'Als versendet markieren',
          className: 'ant-btn ant-btn-primary Button',
        })

    cancelBtn.removeAttribute('id')
    confirmBtn.removeAttribute('id')

    footer.appendChild(cancelBtn)
    footer.appendChild(confirmBtn)
    box.appendChild(title)
    box.appendChild(message)
    box.appendChild(footer)
    overlay.appendChild(box)
    document.body.appendChild(overlay)

    const close = (confirmed) => {
      document.removeEventListener('keydown', onKeyDown)
      overlay.remove()
      resolve(confirmed)
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') close(false)
    }

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close(false)
    })
    cancelBtn.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      close(false)
    })
    confirmBtn.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      close(true)
    })
    document.addEventListener('keydown', onKeyDown)
    cancelBtn.focus()
  })
}

function initializeEditPageMarkAsSentButton() {
  const invitationId = getEditInvitationIdFromPath()
  if (!invitationId) return false

  const rightSide = findPageHeaderActionArea()
  if (!rightSide) return false

  const vorschauVersendenBtn = findHeaderButtonByLabel(rightSide, 'Vorschau & Versenden')
  if (!vorschauVersendenBtn?.parentNode) return false

  bindVorschauVersendenStatusRefresh(vorschauVersendenBtn)

  const existing = document.getElementById(LUX_OWNERS_MEETING_INVITATION_MARK_SENT_BTN_ID)
  if (existing) {
    existing.className = DEFAULT_HEADER_BUTTON_CLASS
    if (existing.nextSibling !== vorschauVersendenBtn) {
      vorschauVersendenBtn.parentNode.insertBefore(existing, vorschauVersendenBtn)
    }
    void applyMarkAsSentButtonAvailability(existing, invitationId)
    return true
  }

  const templateBtn = findDefaultStyleHeaderButton(rightSide)
  const newButton = createLuxHeaderButton({
    id: LUX_OWNERS_MEETING_INVITATION_MARK_SENT_BTN_ID,
    label: 'Als versendet markieren',
    templateBtn,
  })
  newButton.dataset.luxMarkSentInvitationId = String(invitationId)

  const defaultLabel = 'Als versendet markieren'
  newButton.addEventListener('click', async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (newButton.disabled || newButton.dataset.luxLoading === '1') return

    const currentInvitationId = getEditInvitationIdFromPath()
    if (!currentInvitationId) return

    const confirmed = await showMarkAsSentConfirmDialog()
    if (!confirmed) return

    newButton.dataset.luxLoading = '1'
    newButton.disabled = true
    const labelSpan = Array.from(newButton.children).find(
      (el) => el.tagName === 'SPAN' && !el.classList.contains('anticon'),
    )
    if (labelSpan) {
      labelSpan.textContent = 'Bitte warten…'
    } else {
      newButton.textContent = 'Bitte warten…'
    }

    try {
      await postOwnersMeetingInvitationProtocol(currentInvitationId)
      markAsSentStatusCache.invitationId = currentInvitationId
      markAsSentStatusCache.status = 'SENT'
      markAsSentStatusCache.loaded = true
      markAsSentStatusCache.fetchedAt = Date.now()
      newButton.dataset.luxLoading = '0'
      applyMarkAsSentButtonState(newButton, currentInvitationId, 'SENT')
      showToast('Einladung wurde als versendet markiert.', 'success')
      setTimeout(() => window.location.reload(), 1500)
    } catch (error) {
      console.error('Error marking owners meeting invitation as sent:', error)
      showToast(
        `Einladung konnte nicht als versendet markiert werden. ${error?.message || ''}`.trim(),
        'error',
        10000,
      )
      newButton.dataset.luxLoading = '0'
      if (labelSpan) {
        labelSpan.textContent = defaultLabel
      } else {
        newButton.textContent = defaultLabel
      }
      void applyMarkAsSentButtonAvailability(newButton, currentInvitationId)
    }
  })

  vorschauVersendenBtn.parentNode.insertBefore(newButton, vorschauVersendenBtn)
  void applyMarkAsSentButtonAvailability(newButton, invitationId)
  return true
}

function initializeViewPageNachrichtenversandButton() {
  if (document.getElementById(LUX_OWNERS_MEETING_INVITATION_NACHRICHTENVERSAND_BTN_ID)) {
    return true
  }

  const rightSide = findPageHeaderActionArea()
  if (!rightSide) return false

  const invitationId = getViewInvitationIdFromPath()
  if (!invitationId) return false

  const kopierenBtn = findHeaderButtonByLabel(rightSide, 'Kopieren')
  const nachrichtenAnsehenBtn = findHeaderButtonByLabel(rightSide, 'Nachrichten ansehen')
  const canInsertBeforeNachrichten = Boolean(nachrichtenAnsehenBtn?.parentNode)
  const canInsertAfterKopieren = Boolean(kopierenBtn?.parentNode)
  if (!canInsertBeforeNachrichten && !canInsertAfterKopieren) return false

  const templateBtn = findDefaultStyleHeaderButton(rightSide)
  const newButton = createLuxHeaderButton({
    id: LUX_OWNERS_MEETING_INVITATION_NACHRICHTENVERSAND_BTN_ID,
    label: 'Nachrichtenversand',
    templateBtn: kopierenBtn || templateBtn,
    className: kopierenBtn?.className || DEFAULT_HEADER_BUTTON_CLASS,
  })

  newButton.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    window.location.href = `/nachrichtenversand?type=owners-meeting-invitation&sourceIds=${encodeURIComponent(invitationId)}`
  })

  if (canInsertBeforeNachrichten) {
    nachrichtenAnsehenBtn.parentNode.insertBefore(newButton, nachrichtenAnsehenBtn)
  } else {
    kopierenBtn.parentNode.insertBefore(newButton, kopierenBtn.nextSibling)
  }

  return true
}
